import axios from 'axios';

import { API_URL } from '../config/api';
import {
  readChannelMessagesFromRealm,
  readSavedMessagesFromRealm,
  readUsersFromRealm,
  readUsersByIdsFromRealm,
  getRealmDoc,
  upsertRealmDocs,
} from './syncService';
import { getActiveWorkflowByCategory } from '../utils/reactionConfig';
import { stripHtmlForTextExtraction } from '../utils/fileUtils';
import { extractEntityId, normalizeUserRecord } from '../utils/directMessageUtils';

export const resolveUsersByIds = async (ids = [], reduxUsers = []) => {
  const wanted = [...new Set((ids || []).map(extractEntityId).filter(Boolean))];
  if (!wanted.length) return [];
  const byId = new Map();
  (reduxUsers || []).forEach((user) => {
    const id = extractEntityId(user);
    if (id) byId.set(id, user);
  });
  const missing = wanted.filter((id) => !byId.has(id));
  if (missing.length) {
    const fromRealm = await readUsersByIdsFromRealm(missing);
    fromRealm.forEach((user) => {
      const id = extractEntityId(user);
      if (id) byId.set(id, normalizeUserRecord(user));
    });
  }
  const stillMissing = wanted.filter((id) => !byId.has(id));
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  if (stillMissing.length && online) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/users/by-ids`, stillMissing, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetched = Array.isArray(response.data) ? response.data : [];
      fetched.forEach((user) => {
        const id = extractEntityId(user);
        if (id) byId.set(id, normalizeUserRecord(user));
      });
      if (fetched.length) {
        upsertRealmDocs('UserProfile', fetched);
      }
    } catch {
      // Directory lookup is best-effort; DM rows still render from channel name.
    }
  }
  return wanted.map((id) => byId.get(id)).filter(Boolean);
};

export const resolveAllUsers = async (reduxUsers = []) => {
  if (Array.isArray(reduxUsers) && reduxUsers.length) return reduxUsers;
  return (await readUsersFromRealm()) || [];
};

export const resolveLocalMessage = async (messageId, reduxMessagesByChannel = {}) => {
  if (!messageId) return null;
  const fromRealm = await getRealmDoc('Message', messageId);
  if (fromRealm) return fromRealm;
  const lists = Object.values(reduxMessagesByChannel || {});
  for (const list of lists) {
    const found = (list || []).find((msg) => String(msg?.id || msg?._id) === String(messageId));
    if (found) return found;
  }
  return null;
};

export const mergeChannelMessages = (realmMessages, reduxMessages) => {
  const map = new Map();
  (realmMessages || []).forEach((msg) => {
    const id = msg?.id || msg?._id;
    if (id) map.set(String(id), msg);
  });
  (reduxMessages || []).forEach((msg) => {
    const id = msg?.id || msg?._id;
    if (id) map.set(String(id), msg);
  });
  return Array.from(map.values()).sort((a, b) => {
    const at = new Date(a.createdAt || 0).getTime();
    const bt = new Date(b.createdAt || 0).getTime();
    return bt - at;
  });
};

export const collectLocalChannelMessages = async (channelId, reduxMessages = []) => {
  if (!channelId) return [];
  const realmMessages = await readChannelMessagesFromRealm(channelId);
  return mergeChannelMessages(realmMessages, reduxMessages);
};

export const isSharedResourceMessage = (msg) => {
  if (!msg || msg.hideFromChat) return false;
  if (Array.isArray(msg.attachments) && msg.attachments.length > 0) return true;
  if (msg.linkPreview?.url && !msg.linkPreview?.hiddenFromSharedResources) return true;
  return false;
};

export const workflowMetricsFromMessages = (messages = []) => {
  const items = [];
  (messages || []).forEach((msg) => {
    if (!msg || msg.hideFromChat) return;
    const workflowByCategory = getActiveWorkflowByCategory(msg.reactions);
    if (!workflowByCategory || Object.keys(workflowByCategory).length === 0) return;
    items.push({
      messageId: msg.id || msg._id,
      content: stripHtmlForTextExtraction(msg.content) || 'No message text',
      contentHtml: msg.content || '',
      workflowDescription: msg.workflowDescription || null,
      workflowTitle: msg.workflowTitle || null,
      senderId: msg.senderId,
      senderName: msg.senderName || 'Unknown',
      senderProfilePicture: msg.senderProfilePicture || null,
      channelId: msg.channelId || null,
      replyToMessageId: msg.replyToMessageId || null,
      createdAt: msg.createdAt,
      workflowByCategory,
      workflowTrackId: msg.workflowTrackId || null,
      workflowTrackLink: msg.workflowTrackLink || null,
      customTags: msg.customTags || [],
      workflowAssignedToUserId: msg.workflowAssignedToUserId || null,
      workflowAssignedToName: msg.workflowAssignedToName || null,
      completionTimeInMinutes: msg.completionTimeInMinutes || null,
      urgentDeadlineAt: msg.urgentDeadlineAt || null,
    });
  });
  return items;
};

export const hydrateSavedWithStore = (savedList, getState) => {
  const list = Array.isArray(savedList) ? savedList : [];
  if (!list.length || typeof getState !== 'function') return list;
  const messagesByChannel = getState()?.chat?.messages || {};
  const findInStore = (messageId) => {
    if (!messageId) return null;
    for (const msgs of Object.values(messagesByChannel)) {
      const found = (msgs || []).find((m) => m.id === messageId || m._id === messageId);
      if (found) return found;
    }
    return null;
  };
  return list.map((sm) => {
    if (sm?.message?.id || sm?.message?.content || sm?.message?.attachments?.length) {
      return sm;
    }
    const message = findInStore(sm.messageId);
    if (!message) return sm;
    return {
      ...sm,
      message,
      channelId: sm.channelId || message.channelId || null,
    };
  });
};

export const loadLocalSavedMessages = async (getState) => {
  const cached = (await readSavedMessagesFromRealm()) || [];
  return hydrateSavedWithStore(cached, getState);
};

export const paginateSavedMessages = (list, page = 0, size = 20) => {
  const rows = Array.isArray(list) ? list : [];
  const start = Math.max(0, page) * size;
  const slice = rows.slice(start, start + size);
  return {
    savedMessages: slice,
    page,
    hasMore: start + size < rows.length,
    totalElements: rows.length,
  };
};

export const savedChannelsSummaryFromLocal = (savedList = []) => {
  const byChannel = new Map();
  (savedList || []).forEach((sm) => {
    const channelId = sm?.channelId;
    if (!channelId) return;
    const createdAt = sm.createdAt || sm.message?.createdAt || null;
    const existing = byChannel.get(channelId);
    if (!existing) {
      byChannel.set(channelId, { channelId, count: 1, lastSavedAt: createdAt });
      return;
    }
    existing.count += 1;
    if (createdAt && (!existing.lastSavedAt || createdAt > existing.lastSavedAt)) {
      existing.lastSavedAt = createdAt;
    }
  });
  return Array.from(byChannel.values()).sort((a, b) =>
    String(b.lastSavedAt || '').localeCompare(String(a.lastSavedAt || ''))
  );
};

const savedSearchHaystack = (sm) => {
  const message = sm?.message || {};
  const attachmentNames = (message.attachments || [])
    .map((att) => att.fileName || att.originalFileName || '')
    .join(' ');
  return [
    message.content,
    message.senderName,
    sm.channelId,
    attachmentNames,
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/<[^>]*>/g, ' ')
    .toLowerCase();
};

export const searchLocalSavedMessages = (savedList, query, channelId = null) => {
  const q = String(query || '').trim().toLowerCase();
  let rows = savedList || [];
  if (channelId) {
    rows = rows.filter((sm) => sm.channelId === channelId);
  }
  if (!q) return rows;
  return rows.filter((sm) => savedSearchHaystack(sm).includes(q));
};
